/** Valeur d'un paramètre (ou d'une clé de corps) sensible, telle qu'enregistrée. */
export const MASKED = '[masked]'

/**
 * Mots qui rendent un paramètre sensible, quelle que soit sa place dans le nom
 * (`api_key`, `apiKey`, `X-Amz-Signature`, `session_id`…). Liste reprise par le journal des
 * requêtes de l'API (`apps/api/src/lib/sensitive-params.ts`) : garder les deux alignées.
 */
const SENSITIVE_WORDS = new Set([
  'token',
  'key',
  'apikey',
  'secret',
  'password',
  'passwd',
  'pwd',
  'pass',
  'passphrase',
  'mdp',
  'auth',
  'authorization',
  'credential',
  'credentials',
  'signature',
  'sig',
  'email',
  'mail',
  'phone',
  'tel',
  'code',
  'otp',
  'jwt',
  'session',
  'sessionid',
  'sid',
  'ssn',
  'iban',
  'card',
  'cvc',
  'cvv',
  'cvv2',
  'csc',
  'cryptogramme',
  'jeton',
  'cookie',
])

/** Fins de noms collés sensibles (`accesstoken`, `useremail`, `clientsecret`, `motdepasse`…). */
const SENSITIVE_SUFFIXES = [
  'token',
  'secret',
  'password',
  'apikey',
  'email',
  'signature',
  'motdepasse',
]

/** Mots d'un nom (`accessToken`, `access_token`, `X-Access-Token` → `access`, `token`). */
export function nameWords(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== '')
}

/**
 * Le nom `name` (décodé) désigne-t-il une valeur sensible ? Un de ses mots est sensible
 * (`api_key`, `X-Amz-Signature`…), ou il finit, collé, par un nom sensible (`accesstoken`).
 */
export function isSensitiveName(name: string): boolean {
  const words = nameWords(name)
  if (words.some((word) => SENSITIVE_WORDS.has(word))) return true
  const compact = words.join('')
  return SENSITIVE_SUFFIXES.some((suffix) => compact.endsWith(suffix))
}

/** Le paramètre `name` (encodé tel que dans l'URL) porte-t-il une valeur sensible ? */
function isSensitiveParam(name: string): boolean {
  let decoded = name
  try {
    decoded = decodeURIComponent(name.replace(/\+/g, ' '))
  } catch {
    // Nom mal encodé : analysé tel quel.
  }
  return isSensitiveName(decoded)
}

/**
 * Paramètres `a=1&b=2` (requête, fragment ou corps de formulaire) dont les valeurs sensibles
 * deviennent `[masked]` ; le reste est gardé à l'identique (encodage compris).
 */
export function maskParams(params: string): string {
  return params
    .split('&')
    .map((pair) => {
      const separator = pair.indexOf('=')
      if (separator === -1) return pair
      const name = pair.slice(0, separator)
      return isSensitiveParam(name) ? `${name}=${MASKED}` : pair
    })
    .join('&')
}

/**
 * URL enregistrable : identifiants (`user:password@`) retirés, valeurs des paramètres sensibles
 * masquées dans la requête et dans le fragment (jetons OAuth renvoyés dans `#…`).
 */
function cleanUrl(url: URL): string {
  const clean = new URL(url.href)
  clean.username = ''
  clean.password = ''
  const search = clean.search.slice(1)
  const hash = clean.hash.slice(1)
  clean.search = ''
  clean.hash = ''
  let href = clean.href
  if (search) href += `?${maskParams(search)}`
  if (hash) href += `#${hash.includes('=') ? maskParams(hash) : hash}`
  return href
}

/**
 * URL d'une page (celle d'une Exception), nettoyée comme celles du réseau : identifiants retirés,
 * valeurs des paramètres sensibles masquées. Vide si elle ne s'analyse pas.
 */
export function cleanPageUrl(href: string): string {
  try {
    return cleanUrl(new URL(href))
  } catch {
    return ''
  }
}

/**
 * Réécrit l'URL absolue d'une requête avant son enregistrement, ou renvoie `null` pour ne pas
 * l'enregistrer. Le nettoyage automatique s'applique ensuite au résultat.
 */
export type MaskNetworkUrl = (url: string) => string | null

/** URL absolue, ou `null` si elle ne s'analyse pas. */
function absoluteUrl(url: string, base: string): URL | null {
  try {
    return new URL(url, base)
  } catch {
    return null
  }
}

/**
 * URL à enregistrer pour une requête de la page (`url` telle que demandée, relative à `base`),
 * ou `null` pour l'ignorer : requêtes vers l'API belvdr, exclues par `maskUrl` (ou s'il
 * échoue). Dans tous les autres cas, l'URL est nettoyée.
 */
export function urlRecorder({
  apiHost,
  maskUrl,
}: {
  apiHost: string
  maskUrl?: MaskNetworkUrl | undefined
}) {
  const apiOrigin = new URL(apiHost).origin
  return (url: string, base: string): string | null => {
    const absolute = absoluteUrl(url, base)
    if (!absolute || absolute.origin === apiOrigin) return null
    if (!maskUrl) return cleanUrl(absolute)
    let rewritten: unknown
    try {
      rewritten = maskUrl(absolute.href)
    } catch {
      // Hook en échec : rien n'est enregistré plutôt qu'une URL qu'il devait peut-être masquer.
      return null
    }
    const result = typeof rewritten === 'string' ? absoluteUrl(rewritten, base) : null
    return result ? cleanUrl(result) : null
  }
}
